/* ══════════════════════════════════════════════════════════
   EBB — HERO CAROUSEL
   Its own asset, like ebb-product.js, so a throw elsewhere can't take
   the hero down with it.

   Behaviour, all of it deliberate:
   · autoplay pauses on hover, on focus, on touch and when the tab is
     hidden; any manual interaction stops it permanently, because an
     explicit choice outranks a timer
   · the active dot IS the countdown, so autoplay is never a surprise
   · prefers-reduced-motion means no autoplay at all, and no video
   · only the live slide's video plays — the rest are paused and
     external embeds don't even get their src until they go live
   · works in the theme editor: re-initialises on section load, and
     jumps to (and holds) whichever slide block the merchant selects
   ══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (!root || root.hasAttribute('data-hcar-ready')) return;
    root.setAttribute('data-hcar-ready', '1');

    var frame = root.querySelector('[data-hcar-frame]');
    if (!frame) return;
    var slides = Array.prototype.slice.call(frame.querySelectorAll('.hc-slide'));
    if (!slides.length) return;

    var dotsBox = root.querySelector('[data-hcar-dots]');
    var playBtn = root.querySelector('[data-hcar-play]');
    var countEl = root.querySelector('[data-hcar-count]');
    var liveEl = root.querySelector('[data-hcar-live]');
    var prevBtn = root.querySelector('[data-hcar-prev]');
    var nextBtn = root.querySelector('[data-hcar-next]');

    var DUR = parseInt(root.getAttribute('data-speed'), 10);
    if (isNaN(DUR) || DUR <= 0) DUR = 7000;
    /* a seconds value reaching a timer as milliseconds is what made the
       announcement bar flicker — refuse anything implausibly small */
    if (DUR < 100) DUR = DUR * 1000;
    if (DUR < 1500) DUR = 1500;

    var wantsAutoplay = root.getAttribute('data-autoplay') === 'true' && slides.length > 1;
    var pauseOnHover = root.getAttribute('data-pause-hover') !== 'false';

    var i = 0, timer = null, playing = false, userStopped = false, held = false;

    /* ── dots, built from the slide count ─────────────────── */
    var dots = [];
    if (dotsBox && slides.length > 1) {
      dotsBox.innerHTML = '';
      slides.forEach(function (s, n) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'hc-dot';
        d.setAttribute('aria-label', 'Slide ' + (n + 1) + ' of ' + slides.length);
        d.setAttribute('aria-current', String(n === 0));
        d.innerHTML = '<i aria-hidden="true"></i>';
        d.addEventListener('click', function () { stopForGood(); go(n); });
        dotsBox.appendChild(d);
      });
      dots = Array.prototype.slice.call(dotsBox.children);
    }

    function slideLabel(n) {
      return (slides[n].getAttribute('aria-label') || '').replace(/^\d+ of \d+:?\s*/, '');
    }

    /* ── media: only the live slide plays ─────────────────── */
    function syncMedia() {
      slides.forEach(function (s, k) {
        var live = k === i;

        var vid = s.querySelector('video');
        if (vid) {
          if (live && !reduced.matches) {
            vid.muted = true;                 // required for autoplay everywhere
            var p = vid.play();
            if (p && p.catch) p.catch(function () { /* blocked; the poster stands in */ });
          } else {
            try { vid.pause(); } catch (e) { /* nothing to do */ }
          }
        }

        var frameEl = s.querySelector('[data-hcar-embed]');
        if (frameEl) {
          var src = frameEl.getAttribute('data-hcar-embed');
          if (live && !reduced.matches) {
            /* the src lands only when the slide goes live, so a carousel
               of embeds costs one third-party request, not three */
            if (frameEl.getAttribute('src') !== src) frameEl.setAttribute('src', src);
          } else if (frameEl.hasAttribute('src')) {
            frameEl.removeAttribute('src');
          }
        }
      });
    }

    /* ── the move ─────────────────────────────────────────── */
    function go(n, quiet) {
      i = ((n % slides.length) + slides.length) % slides.length;

      slides.forEach(function (s, k) {
        var on = k === i;
        s.classList.toggle('is-on', on);
        /* visibility:hidden already drops the inactive slide's links out of
           the tab order; aria-hidden takes it out of the a11y tree too */
        s.setAttribute('aria-hidden', String(!on));
      });
      dots.forEach(function (d, k) { d.setAttribute('aria-current', String(k === i)); });

      if (countEl) countEl.textContent = (i + 1) + ' / ' + slides.length;
      if (!quiet && liveEl) {
        liveEl.textContent = 'Slide ' + (i + 1) + ' of ' + slides.length +
          (slideLabel(i) ? ': ' + slideLabel(i) : '');
      }

      syncMedia();
      restartFill();
      if (playing) schedule();
    }

    /* re-trigger the dot's fill so the countdown starts from zero */
    function restartFill() {
      var fill = dots[i] && dots[i].firstElementChild;
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.getBoundingClientRect();
      fill.style.animation = '';
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () { go(i + 1, true); }, DUR);
    }

    function play() {
      if (userStopped || held || reduced.matches || !wantsAutoplay) return;
      playing = true;
      root.classList.add('is-playing');
      setPlayBtn(true);
      restartFill();
      schedule();
    }

    function pause(soft) {
      playing = false;
      clearTimeout(timer);
      root.classList.remove('is-playing');
      if (!soft) setPlayBtn(false);
    }

    /* an explicit choice outranks a timer */
    function stopForGood() { userStopped = true; pause(); }

    function setPlayBtn(isPlaying) {
      if (!playBtn) return;
      playBtn.setAttribute('aria-label', isPlaying ? 'Pause the carousel' : 'Play the carousel');
      playBtn.innerHTML = '<i class="fa-solid fa-' + (isPlaying ? 'pause' : 'play') + '" aria-hidden="true"></i>';
    }

    /* ── wiring ───────────────────────────────────────────── */
    if (playBtn) {
      playBtn.addEventListener('click', function () {
        if (playing) { stopForGood(); } else { userStopped = false; play(); }
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { stopForGood(); go(i - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { stopForGood(); go(i + 1); });

    if (pauseOnHover) {
      root.addEventListener('pointerenter', function () { if (playing) pause(true); });
      root.addEventListener('pointerleave', function () { if (!userStopped && !held) play(); });
      root.addEventListener('focusin', function () { if (playing) pause(true); });
      root.addEventListener('focusout', function () {
        if (!userStopped && !held && !root.contains(document.activeElement)) play();
      });
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause(true); else if (!userStopped && !held) play();
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { stopForGood(); go(i - 1); }
      else if (e.key === 'ArrowRight') { stopForGood(); go(i + 1); }
      else return;
      e.preventDefault();
    });

    /* ── swipe, with an axis lock so vertical scrolling still works ── */
    var sx = 0, sy = 0, tracking = false, locked = null;
    frame.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      sx = e.clientX; sy = e.clientY; tracking = true; locked = null;
      if (playing) pause(true);
    }, { passive: true });
    frame.addEventListener('pointermove', function (e) {
      if (!tracking) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (locked === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }, { passive: true });
    frame.addEventListener('pointerup', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.clientX - sx;
      if (locked === 'x' && Math.abs(dx) > 45) { stopForGood(); go(i + (dx < 0 ? 1 : -1)); }
      else if (!userStopped && !held) play();
    }, { passive: true });
    frame.addEventListener('pointercancel', function () { tracking = false; }, { passive: true });

    /* ── theme editor: hold on the block the merchant is editing ── */
    root.addEventListener('ebb:hcar:select', function (e) {
      var n = slides.indexOf(e.detail.slide);
      if (n < 0) return;
      held = true;
      pause();
      go(n);
    });
    root.addEventListener('ebb:hcar:deselect', function () {
      held = false;
      if (!userStopped) play();
    });

    /* ── start ────────────────────────────────────────────── */
    go(0, true);
    if (reduced.matches || !wantsAutoplay) {
      pause();
      if (playBtn) playBtn.hidden = true;      // nothing to pause
    } else {
      play();
    }
    reduced.addEventListener('change', function (e) {
      if (e.matches) { pause(); if (playBtn) playBtn.hidden = true; }
      else { if (playBtn) playBtn.hidden = false; if (!userStopped && !held) play(); }
      syncMedia();
    });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ebb-hcar]');
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* ── Shopify theme editor ─────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var r = e.target.querySelector('[data-ebb-hcar]');
    if (r) r.removeAttribute('data-hcar-ready');
  });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest ? e.target.closest('.hc-slide') : null;
    if (!slide) return;
    var root = slide.closest('[data-ebb-hcar]');
    if (root) root.dispatchEvent(new CustomEvent('ebb:hcar:select', { detail: { slide: slide } }));
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var slide = e.target.closest ? e.target.closest('.hc-slide') : null;
    if (!slide) return;
    var root = slide.closest('[data-ebb-hcar]');
    if (root) root.dispatchEvent(new CustomEvent('ebb:hcar:deselect'));
  });
})();
